import { useState } from 'react';

import { EllipsisVerticalIcon, RefreshCwIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import type { ApiError } from '@/api/fetcher';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getPermissions } from '@/redux/permissions/selectors';
import type { AccountDebt } from '@/shared/types/contacts';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import { ChangeDebtStatusDialog } from './ChangeDebtStatusDialog';
import { useDebts } from '../hooks/useDebts';

interface DebtActionsCellProps {
  debt: AccountDebt;
}

export const DebtActionsCell = ({ debt }: DebtActionsCellProps) => {
  const { t } = useTranslation();
  const { deleteDebt, isDeleting } = useDebts();
  const canDelete = useSelector((state: RootState) =>
    getPermissions(state, 'contacts', actions.DELETE)
  );
  const canWrite = useSelector((state: RootState) => getPermissions(state, 'debts', actions.WRITE));
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [force, setForce] = useState(false);
  const [conflictMessage, setConflictMessage] = useState<string>();

  const handleOpenChange = (open: boolean) => {
    setIsDeleteOpen(open);
    if (!open) {
      setForce(false);
      setConflictMessage(undefined);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteDebt({ debt, force });
      toast.success(t('contacts.debts.deleted'));
      handleOpenChange(false);
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError.statusCode === 409 && apiError.code === 'contact-has-flagged-debts') {
        setConflictMessage(apiError.message);
        return;
      }
      const message = error instanceof Error ? error.message : t('contacts.debts.delete_failed');
      toast.error(message);
    }
  };

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            variant="ghost-neutral"
            size="icon-sm"
            disabled={!canDelete && !canWrite}
            aria-label={t('common.more')}
          >
            <EllipsisVerticalIcon size={16} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          {canWrite && (
            <DropdownMenuItem
              className="flex items-center gap-2"
              onSelect={() => setIsStatusOpen(true)}
            >
              <RefreshCwIcon size={16} />
              {t('contacts.debts.change_status')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            variant="destructive"
            className="flex items-center gap-2"
            disabled={!canDelete}
            onSelect={() => setIsDeleteOpen(true)}
          >
            <Trash2Icon size={16} />
            {t('common.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {isStatusOpen && (
        <div role="presentation" onClick={(e) => e.stopPropagation()}>
          <ChangeDebtStatusDialog debt={debt} onOpenChange={setIsStatusOpen} />
        </div>
      )}

      <AlertDialog open={isDeleteOpen} onOpenChange={handleOpenChange}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('contacts.debts.delete_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('contacts.debts.delete_description', { 0: debt.debt_external_id || debt.debt_id })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id={`forceDeleteDebt-${debt.debt_id}`}
                checked={force}
                onCheckedChange={(checked) => setForce(checked as boolean)}
              />
              <label
                htmlFor={`forceDeleteDebt-${debt.debt_id}`}
                className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                {t('contacts.debts.delete_force')}
              </label>
            </div>
            <div className="mt-1 ml-6 text-xs text-foreground-muted">
              {t('contacts.debts.delete_force_help')}
            </div>
            {conflictMessage && (
              <p role="alert" className="mt-2 text-sm text-destructive">
                {conflictMessage}
              </p>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <Button variant="destructive" isLoading={isDeleting} onClick={handleConfirmDelete}>
              {t('common.delete')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
